import React from 'react';
import { Link } from 'react-router-dom';

import { BLOG_POSTS } from './blogData';
import { usePublicSitePath } from './publicSitePaths';
import { PublicLanguage } from './types';

interface BlogPageProps {
  language: PublicLanguage;
}

const BLOG_UI_COPY: Record<
  PublicLanguage,
  {
    heroTitle: string;
    heroSubtitle: string;
    readArticle: string;
    readTime: string;
    published: string;
  }
> = {
  en: {
    heroTitle: 'Car Buying and Ownership Insights',
    heroSubtitle:
      'Actionable guides on buying, maintaining, and getting long-term value from your used vehicle.',
    readArticle: 'Read Article',
    readTime: 'min read',
    published: 'Published',
  },
  es: {
    heroTitle: 'Consejos de Compra y Propiedad de Autos',
    heroSubtitle:
      'Guias practicas sobre compra, mantenimiento y valor a largo plazo para tu auto usado.',
    readArticle: 'Leer Articulo',
    readTime: 'min de lectura',
    published: 'Publicado',
  },
};

const BlogPage: React.FC<BlogPageProps> = ({ language }) => {
  const ui = BLOG_UI_COPY[language];
  const publicSitePath = usePublicSitePath();

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <section className="mb-10 rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50 p-6 md:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-blue-700">UAB Blog</p>
        <h1 className="mt-2 text-3xl md:text-4xl font-bold text-slate-900">{ui.heroTitle}</h1>
        <p className="mt-3 text-slate-700 max-w-3xl">{ui.heroSubtitle}</p>
      </section>

      <section className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {BLOG_POSTS.map((post) => (
          <article
            key={post.slug}
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow"
          >
            <img
              src={post.imageUrl}
              alt={post.title[language]}
              className="h-56 w-full object-cover"
              loading="lazy"
            />
            <div className="p-6">
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-blue-700">
                <span className="rounded-full bg-blue-100 px-2.5 py-1">{post.category}</span>
                <span className="text-slate-500">
                  {ui.published}: {post.publishedAt}
                </span>
                <span className="text-slate-500">
                  {post.readTimeMinutes} {ui.readTime}
                </span>
              </div>

              <h2 className="mt-3 text-2xl font-bold text-slate-900 leading-tight">
                {post.title[language]}
              </h2>
              <p className="mt-3 text-slate-700">{post.excerpt[language]}</p>

              <div className="mt-4 flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600"
                  >
                    #{tag.replace(/\s+/g, '-')}
                  </span>
                ))}
              </div>

              <Link
                to={publicSitePath(`/blog/${post.slug}`)}
                className="mt-6 inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                {ui.readArticle}
              </Link>
            </div>
          </article>
        ))}
      </section>
    </main>
  );
};

export default BlogPage;
